import React, { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, ChevronDown, ChevronUp, Send, Bell, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import {
  loadMoreMessagesApi,
  sendMessageApi,
  markAllMessagesAsReadApi,
  getParentApi
} from "../services/assistantChatService.jsx";
import {
  initializeEcho,
  subscribeToAssistantChat,
  unsubscribeFromAssistantChat,
} from "../services/echoService";
import {
  generatePatientContextMessage,
  getPatientDataFromSession,
} from "../utils/patientContextFormatter.js";
import {
  useAssistantChatroom,
  useAssistantMessages,
  addMessageToAssistantCache,
  prependMessagesToAssistantCache,
} from "../queries";
import {
  selectAssistantExpanded,
  selectAssistantUnreadCount,
  selectIsSendingAssistantMessage,
  setAssistantExpanded,
  incrementAssistantUnreadCount,
  decrementAssistantUnreadCount,
  resetAssistantUnreadCount,
  setActiveChatroomId,
  setIsSendingAssistantMessage,
} from "../store";

import { API_BASE_URL } from "@/app_url";
const Assistant = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  // Redux UI State
  const assistantExpanded = useSelector(selectAssistantExpanded);
  const unreadCount = useSelector(selectAssistantUnreadCount);
  const isSending = useSelector(selectIsSendingAssistantMessage);

  // Local component state
  const [message, setMessage] = useState("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userInfo, setUserInfo] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("doctolikLoginInfo"));
    } catch {
      return null;
    }
  });

  const messagesContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // 1. Fetch / Retrieve Cached Chatroom via TanStack Query (24h / all day persistent cache)
  const {
    data: chatroomData,
    isLoading: isChatroomLoading,
  } = useAssistantChatroom(userInfo);

  const chatroomId = chatroomData?.id || null;

  // Sync active chatroom ID to Redux
  useEffect(() => {
    if (chatroomId) {
      dispatch(setActiveChatroomId(chatroomId));
    }
  }, [chatroomId, dispatch]);

  // 2. Fetch / Retrieve Cached Messages via TanStack Query (24h / all day persistent cache)
  const {
    data: messagesData,
    isLoading: isMessagesLoading,
  } = useAssistantMessages(chatroomId);

  const messages = messagesData?.messages || [];
  const hasMoreMessages = messagesData?.hasMore ?? true;
  const oldestMessageId = messagesData?.oldestMessageId ?? null;

  // Initializing state (only true if we have no cached messages and are loading)
  const isInitializing =
    (isChatroomLoading && !chatroomData) ||
    (Boolean(chatroomId) && isMessagesLoading && messages.length === 0);

  // Scroll messages container to bottom
  const scrollToBottom = useCallback((delay = 100) => {
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, delay);
  }, []);

  // Scroll to bottom when messages initially load
  useEffect(() => {
    if (messages.length > 0 && !isLoadingMore) {
      scrollToBottom();
    }
  }, [chatroomId, scrollToBottom]);

  const [subscribeChannelId, setSubscribeChannelId] = useState(null);

  // 3. Initialize WebSocket & subscribe to real-time events
  useEffect(() => {
    const info = userInfo || JSON.parse(localStorage.getItem("doctolikLoginInfo") || "null");
    if (!userInfo && info) {
      setUserInfo(info);
    }

    if (info?.users?.id) {
      setCurrentUserId(info.users.id);

      // Initialize WebSocket
      const token = info.authorisation?.token;
      if (token) {
        initializeEcho(token);
      }

      if(  info.users.type === "Nurse" ){
        // check for stored parent
        if( info.users.parent_id ){
          setSubscribeChannelId(info.users.parent_id);
        }

        getParentApi(info.users.id, {
          onlyId: true,
        })
          .then((response) => {
            console.log("Fetched data:", response.data);
            if (response.data?.parent_id) {
              console.log("Fetched parent ID:", response.data?.parent_id);
              setSubscribeChannelId(response.data?.parent_id);
            }
          })
          .catch((error) => {
            console.error("Error fetching parent ID:", error);
          });
      }else{
        setSubscribeChannelId(info.users.id);
      }

      if (!subscribeChannelId) {
        console.error("No valid subscribeChannelId found for user:", info.users);
        return;
      }

      // Subscribe to real-time events
      subscribeToAssistantChat(subscribeChannelId, {
        onMessageSent: (event) => {
          // Push incoming message directly into TanStack Query persistent cache without refetching
          if (event?.message && event.message.sender_id !== info.users.id) {
            const targetChatroomId = event.message.chatroom_id || chatroomId;
            if (targetChatroomId) {
              addMessageToAssistantCache(queryClient, targetChatroomId, event.message);
            }
            scrollToBottom();

            // Increment unread count if chat is collapsed
            if (!assistantExpanded) {
              dispatch(incrementAssistantUnreadCount());
            }
          }
        },
        onMessageRead: (event) => {
          console.log("Message read:", event?.message_id);
          dispatch(decrementAssistantUnreadCount());
        },
        onMemberAdded: (event) => {
          console.log("Member added:", event?.user);
        },
        onMemberRemoved: (event) => {
          console.log("Member removed:", event?.user_id);
        },
      });

      // Cleanup on unmount
      return () => {
        unsubscribeFromAssistantChat(subscribeChannelId);
      };
    } else {
      console.error("User info not found in localStorage", info?.users);
    }
  }, [userInfo, chatroomId, assistantExpanded, dispatch, queryClient, scrollToBottom, subscribeChannelId]);

  const handleRingBell = async () => {
    if (!chatroomId) return;
    const bellMessage = "🔔 Bell notification - Doctor needs attention!";
    try {
      const response = await sendMessageApi(chatroomId, bellMessage, "text", {
        is_bell_notification: true,
      });
      if (response?.data?.success && response?.data?.data) {
        // Push message directly into persistent cache
        addMessageToAssistantCache(queryClient, chatroomId, response.data.data);
        scrollToBottom();
      }
    } catch (error) {
      console.error("Failed to send bell notification:", error);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    if (!chatroomId) return;

    try {
      dispatch(setIsSendingAssistantMessage(true));
      const response = await sendMessageApi(chatroomId, message.trim(), "text");

      if (response?.data?.success && response?.data?.data) {
        // Push message directly into TanStack Query persistent cache without refetching
        addMessageToAssistantCache(queryClient, chatroomId, response.data.data);
        scrollToBottom();
      }

      setMessage("");

      // Send hidden patient context message after the user message
      try {
        const patientData = getPatientDataFromSession();
        if (patientData) {
          const patientContextMsg = generatePatientContextMessage(patientData);
          if (patientContextMsg) {
            setTimeout(async () => {
              try {
                await sendMessageApi(chatroomId, patientContextMsg, "system");
              } catch (contextError) {
                console.warn("Failed to send patient context message:", contextError);
              }
            }, 300);
          }
        }
      } catch (contextError) {
        console.warn("Error generating patient context:", contextError);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      dispatch(setIsSendingAssistantMessage(false));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleExpandToggle = async () => {
    const nextState = !assistantExpanded;
    dispatch(setAssistantExpanded(nextState));

    if (nextState && chatroomId) {
      // Mark all messages as read when opening
      try {
        await markAllMessagesAsReadApi(chatroomId);
        dispatch(resetAssistantUnreadCount());
      } catch (error) {
        console.error("Failed to mark messages as read:", error);
      }
    }
  };

  const formatTime = (date) => {
    if (!date) return "";
    const messageDate = new Date(date);
    return messageDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const loadMoreMessages = async () => {
    if (isLoadingMore || !hasMoreMessages || !chatroomId || !oldestMessageId) return;

    setIsLoadingMore(true);
    const previousScrollHeight = messagesContainerRef.current?.scrollHeight || 0;

    try {
      const response = await loadMoreMessagesApi(chatroomId, oldestMessageId);
      if (response?.data?.success && response?.data?.data) {
        const { messages: newMessages = [], has_more, oldest_message_id } = response.data.data;

        // Prepend older messages directly to persisted TanStack Query cache
        prependMessagesToAssistantCache(
          queryClient,
          chatroomId,
          newMessages.slice().reverse(),
          has_more,
          oldest_message_id
        );

        // Maintain scroll position
        setTimeout(() => {
          if (messagesContainerRef.current) {
            const newScrollHeight = messagesContainerRef.current.scrollHeight;
            messagesContainerRef.current.scrollTop = newScrollHeight - previousScrollHeight;
          }
        }, 100);
      }
    } catch (error) {
      console.error("Failed to load more messages:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop } = e.target;
    scrollPositionRef.current = scrollTop;

    // Check if scrolled to top (with threshold)
    if (scrollTop < 50 && !isLoadingMore && hasMoreMessages) {
      loadMoreMessages();
    }
  };

  const isCurrentUserMessage = (msg) => {
    return msg?.sender_id === currentUserId;
  };

  return (
    <li className="expandable-section assistant-section">
      <div className="expandable-header" onClick={handleExpandToggle}>
        <div className="expandable-header-content">
          <div className="iconDiv">
            <MessageCircle size={20} />
          </div>
          <span>Team Chat</span>
          {unreadCount > 0 && <span className="badge-count"></span>}
        </div>
        <div className="chevron-icon">
          {assistantExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>
      {assistantExpanded && (
        <div className="assistant-content">
          {isInitializing ? (
            <div className="loading-container">
              <Loader2 size={24} className="spinner" />
              <span>Loading chat...</span>
            </div>
          ) : (
            <>
              {/* Messages from Team */}
              {messages.length > 0 && (
                <div
                  className="assistant-messages"
                  ref={messagesContainerRef}
                  onScroll={handleScroll}
                >
                  {isLoadingMore && (
                    <div className="loading-more-indicator">
                      <Loader2 size={16} className="spinner" />
                      <span>Loading more messages...</span>
                    </div>
                  )}
                  {!hasMoreMessages && messages.length > 5 && (
                    <div className="no-more-messages">
                      <span>No more messages</span>
                    </div>
                  )}
                  {messages.map((msg) => {
                    const isCurrentUser = isCurrentUserMessage(msg);
                    return (
                      <div
                        key={msg.id}
                        className={`assistant-message-item ${isCurrentUser ? "sent" : "received"}`}
                      >
                        <div className="message-header">
                          <span className="sender-name">
                            {isCurrentUser ? "You" : msg.sender?.name || "Unknown"}
                          </span>
                          <span className="message-time">{formatTime(msg.created_at)}</span>
                        </div>
                        <div className="message-text">{msg.message}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="assistant-input-wrapper">
                <textarea
                  className="assistant-textarea"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Message your assistant..."
                  rows={2}
                  disabled={isSending}
                />
              </div>

              <div className="assistant-actions">
                <button
                  className="assistant-action-btn ring-bell-btn"
                  onClick={handleRingBell}
                  disabled={isSending}
                  title="Ring Bell"
                >
                  <Bell size={16} />
                  <span>Ring Bell</span>
                </button>
                <button
                  className="assistant-action-btn send-btn"
                  onClick={handleSendMessage}
                  disabled={!message.trim() || isSending}
                  title="Send Message"
                >
                  {isSending ? (
                    <Loader2 size={16} className="spinner" />
                  ) : (
                    <Send size={16} />
                  )}
                  <span>{isSending ? "Sending..." : "Send"}</span>
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
};

export default Assistant;
